import queryString from 'query-string';

// Order Management UI Admins (COAM GROUP) - auth0 client IDs.
const DIRECT_ACCESS_AUTH_CLIENT_ID = 'iL1tKEcH1Dc0rWBDvYk2fttkiio4V3Ph';
const INTERNAL_VIEW_AUTH_CLIENT_ID = 'Fc7PweVXeEC68mhnTB6aCt2dDV5uykwg';

// https://gitlab.com/vistaprint-org/technology-operations/web-platform/warhol/identity-product-line/vistaprint-auth-js/-/tree/master/auth
const redirectToOriginLocationAfterSignout = () =>
  window.location.href.toString();

// Auth confiuration objects
export const INTERNAL_VIEW_AUTH_CONFIG = {
  options: {
    connection: 'vistaprintcom-waad',
    redirectAfterSignOutCallback: redirectToOriginLocationAfterSignout,
  },
  developmentMode: { clientID: INTERNAL_VIEW_AUTH_CLIENT_ID },
};
export const DIRECT_ACCESS_LOCALHOST_AUTH_CONFIG = {
  developmentMode: { clientID: DIRECT_ACCESS_AUTH_CLIENT_ID },
  options: {
    redirectAfterSignOutCallback: redirectToOriginLocationAfterSignout,
  },
};
export const DIRECT_ACCESS_REMOTE_AUTH_CONFIG = {
  options: {
    redirectAfterSignOutCallback: redirectToOriginLocationAfterSignout,
  },
  developmentMode: { clientID: DIRECT_ACCESS_AUTH_CLIENT_ID },
};
export const PROXY_ACCESS_PRODUCTION_AUTH_CONFIG = {
  developmentMode: false,
};

const INTERNAL_VIEW_QUERY_STRING_KEY = 'internalView';

/**
 * The (3) different types of application entry points.
 *
 * Authorization specifications require OM applications be conscious of the request origin. In order to support
 * non-default Auth0 Production Client environments, applications  need to initialize auth library under different clients. Additionally,
 * supporting an internal view for our application requires running under a different auth clientID and configuration
 * than accessing our applications as a shopper.
 *
 * (1) Direct Access Remote Entry: This entry type indicates that the end user has directly issues a network request to our
 * assets' primary hosting location. For example, a direct browser request to https://cartui.orders.vpsvc.com
 *
 * (2) Direct Access Local Entry: This entry type indicates that the end user is accessing our applications on a local filesystem.
 *
 * (3) Proxy Access Entry: This entry type is reserved for access to our applications from non-OM maintained origins.
 * Examples include: Web-Platform ingress routing requests to vistaprint.ie, vistaprint.pt, promotique.ie, to our resources.
 */
export const DIRECT_ACCESS_REMOTE_ENTRY = 'direct-remote-entry';
export const DIRECT_ACCESS_LOCAL_ENTRY = 'localhost-entry';
export const PROXY_ACCESS_ENTRY = 'proxy-entry';

/**
 * Regular expressions for origin matching.
 * Any valid vpsvc subdomain will be an allowable callback from the auth provider.
 *
 * For full auth functionality to work, you must ALSO configure the client to support the published location of this application
 * as an 'Allowed Web Origin' on https://developer.cimpress.io/clients/iL1tKEcH1Dc0rWBDvYk2fttkiio4V3Ph/settings.
 */
// TODO: Update this with new cloudfront dns for cart UI.
const directAccessRemoteRegExp = /^https?:\/\/(.+\.vpsvc\.com)/;
const directAccessLocalRegExp = /^https?:\/\/localhost(:\d+)?/;

/**
 * A browser-dependent function to derive the type of application entry mode.
 */
export const determineEntryTypeFromOrigin = () => {
  if (typeof window === `undefined`) {
    return null;
  }
  const { origin } = window.location;
  if (!origin) return null;

  const directAccessRemoteEntry =
    origin.match(directAccessRemoteRegExp) !== null;

  const directAccessLocalEntry = origin.match(directAccessLocalRegExp) !== null;

  if (directAccessRemoteEntry) return DIRECT_ACCESS_REMOTE_ENTRY;
  if (directAccessLocalEntry) return DIRECT_ACCESS_LOCAL_ENTRY;
  return PROXY_ACCESS_ENTRY;
};

/**
 * A browser function to derive if the requestor wants to run the application via
 * the internal view.
 */
export const requestForInternalView = () => {
  if (typeof window === `undefined`) {
    return false;
  }

  const { search } = window.location;
  if (!search) return false;

  const params = queryString.parse(search);

  return Object.keys(params).includes(INTERNAL_VIEW_QUERY_STRING_KEY);
};

const buildAuthConfiguration = enterInternalView => {
  if (enterInternalView) {
    return INTERNAL_VIEW_AUTH_CONFIG;
  }

  const applicationEntryType = determineEntryTypeFromOrigin();
  switch (applicationEntryType) {
    case DIRECT_ACCESS_LOCAL_ENTRY:
      return DIRECT_ACCESS_LOCALHOST_AUTH_CONFIG;
    case DIRECT_ACCESS_REMOTE_ENTRY:
      return DIRECT_ACCESS_REMOTE_AUTH_CONFIG;
    case PROXY_ACCESS_ENTRY:
      return PROXY_ACCESS_PRODUCTION_AUTH_CONFIG;
    default:
      console.error(`Error building auth configuration from host.`);
      return PROXY_ACCESS_PRODUCTION_AUTH_CONFIG;
  }
};

/**
 * An auth config builder based upon which origin the application requestor resides, and if the requestor
 * wants to access the application via the internal portal.
 * 
 * @param {*} culture The configuration object has to contain the culture field. 
 * This field has to follow RFC 5646 (www.rfc-editor.org/rfc/rfc5646.txt), 
 * Tags for the Identification of Languages, where allowed subtags are language, script and region. 
 * For more information see guidlines for culture codes. 'en-IE' by default.
 * (https://gitlab.com/vistaprint-org/technology-operations/web-platform/warhol/site-configuration-product-line/culture-resolution-js-library/tree/master)
 * Example:
      {
          culture: "en-IE"
      }
 * @param {string} authSite The configuration object has to contain the site field
 * Examples: "Vistaprint", "PayPal"
 */
export const buildAuthConfigurationByHostEntry = (
  culture = 'en-IE',
  authSite = null
) => {
  // server-side-rendering safety
  if (typeof window === `undefined`) {
    return;
  }

  const enterInternalView = requestForInternalView();
  const authConfig = buildAuthConfiguration(enterInternalView);

  if (authSite) {
    if (authConfig.options) {
      authConfig.options.site = authSite;
      authConfig.options.requireSession = true;
    } else {
      authConfig.options = { site: authSite, requireSession: true };
    }
  }

  return {
    culture,
    ...authConfig,
  };
};
